/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './PowerBI';
import { toOptions } from 'componentregistry/utils/ManagerUtils';

const PowerBIManager: ComponentManagerFn<'PowerBI'> = (platform: Platform) => {
  return {
    ...baseComponentManager<'PowerBI'>(platform, 'PowerBI'),
    type: 'PowerBI',
    name: 'Power BI',
    isChildAllowed: (page, item, childType) =>
      (['ActionSetBIFilterParam'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      type: {
        label: 'Type',
        type: 'select',
        options: toOptions(['report', 'dashboard', 'visual']),
        category: 'Genereal',
      },
      embedUrl: {
        label: 'Embed Url',
        type: 'expression',
        returnType: async () => 'string',
        required: true,
        category: 'Genereal',
      },
      reportId: {
        label: 'Report Id',
        type: 'expression',
        returnType: async () => 'string',
        required: true,
        category: 'Genereal',
      },
      workspaceId: {
        label: 'Workspace Id',
        type: 'expression',
        returnType: async () => 'string',
        required: true,
        category: 'Genereal',
      },
      dashboardId: {
        label: 'Dashboard Id',
        type: 'expression',
        returnType: async () => 'string',
        required: true,
        category: 'Genereal',
      },
      pageName: {
        label: 'Page Name',
        type: 'expression',
        returnType: async () => 'string',
        category: 'Genereal',
      },
      visualName: {
        label: 'Visual Name',
        type: 'expression',
        returnType: async () => 'string',
        category: 'Genereal',
      },
      allowEdit: {
        label: 'Edit Enabled',
        type: 'boolean',
        category: 'Settings',
      },
      background: {
        label: 'Background',
        type: 'select',
        options: toOptions(['default', 'transparent']),
        category: 'Settings',
      },
      bookmarksPaneEnabled: {
        label: 'Bookmarks Content Enabled',
        type: 'boolean',
        category: 'Settings',
      },
      navContentPaneEnabled: {
        label: 'Nav Content Enabled',
        type: 'boolean',
        category: 'Settings',
      },
      filterPaneEnabled: {
        label: 'Filter Panel Enabled',
        type: 'boolean',
        category: 'Settings',
      },
      displayOption: {
        label: 'Display Option',
        type: 'select',
        options: toOptions(['fitToPage', 'fitToWidth', 'actualSize']),
        category: 'Settings',
      },
      mode: {
        label: 'Mode',
        type: 'select',
        options: toOptions(['visible', 'hidden']),
        category: 'Settings',
      },
    },
    isPropertyVisible(prop, itemProps): boolean {
      const type = itemProps.type;
      switch (prop) {
        case 'pageName':
          return type === 'visual' || type === 'report';
        case 'visualName':
          return type === 'visual';
        case 'reportId':
          return type !== 'dashboard';
        case 'dashboardId':
          return type === 'dashboard';
        case 'allowEdit':
          return type === 'report';
        default:
          return true;
      }
    },

    onPropertyChange: (property, value) => {
      if (property === 'allowEdit' && typeof value === 'boolean') {
        return {
          [property]: value,
          navContentPaneEnabled: value,
          filterPaneEnabled: value,
        };
      }
      return {
        [property]: value,
      };
    },

    getDisplayText: (item) => item.props.reportId.value ?? '',
    category: 'Power BI',
    layout: 'sizeAware',
    icon: { iconName: 'chart-bar' },
    description: `**Overview**<br/>
The Power BI component allows you to seamlessly embed Microsoft Power BI analytics directly into your application. It supports embedding three distinct types of content—Reports, Dashboards, and individual Visuals—providing users with interactive data visualization capabilities without leaving the platform interface.
Use this component to deliver business intelligence dashboards, detailed analytical reports, or specific data charts within your application's pages.

**1. Embedding Types**<br/>
You must first select the **Type** of content you wish to embed, as this determines which identifiers are required.
* **Report:** Embeds a multi-page interactive report. This type supports the most extensive configuration options, including edit mode and navigation panes.
* **Dashboard:** Embeds a single-page overview containing pinned visuals from various reports. Dashboards are generally read-only snapshots.
* **Visual:** Embeds a single specific chart or graph from a report, useful for focused data presentation within a smaller UI container.

**2. Configuration & Identity**<br/>
To successfully load content, you must provide specific identity fields. The component dynamically shows or hides these properties based on the selected Type.
* **Embed URL (Required):** The secure link provided by the Power BI service for embedding.
* **Workspace ID (Required):** The GUID of the Power BI workspace hosting the content.
* **Report ID:** Required for **Report** and **Visual** types.
* **Dashboard ID:** Required only for the **Dashboard** type.
* **Page Name:** Optional for Reports; required for **Visuals** to locate the specific page hosting the chart.
* **Visual Name:** Required only for the **Visual** type to identify the specific chart to render.

**3. Display & Layout Settings**<br/>
Customize how the embedded content fits into your page layout to ensure a seamless user experience.
* **Display Option:** * **Fit to Page:** Scales the content to fill the container perfectly.
    * **Fit to Width:** Scales content to match the width, potentially allowing for vertical scrolling.
    * **Actual Size:** Renders the report at its native pixel resolution.
* **Background:** Choose between **Default** (opaque) or **Transparent** to blend the chart into your application's theme.
* **Mode:** Toggle visibility between **Visible** and **Hidden**.

**4. Interactivity & Editing**<br/>
* **Allow Edit (Reports only):** When enabled, users with appropriate permissions can modify the report directly inside the application. This automatically enables the Navigation and Filter panes.
* **Panes:** You can manually toggle specific UI elements for viewers:
    * **Filter Panel Enabled:** Shows/hides the sidebar for slicing data.
    * **Nav Content Enabled:** Shows/hides page tabs at the bottom.
    * **Bookmarks Content Enabled:** Shows/hides saved report states.

**5. Filtering**<br/>
* **ActionSetBIFilterParam:** This component supports a specific child type, **ActionSetBIFilterParam**. 
* **Dynamic Context:** This allows you to pass parameters dynamically to the embedded report from your application logic (e.g., programmatically filtering the report to show only data for "Region = North" based on a user's selection in a separate dropdown).

**6. Best Use Cases**<br/>
* **Executive Dashboards:** Embedding high-level KPIs and business health snapshots on a home page.
* **Operational Analytics:** Providing detailed, interactive reports within specific functional areas (e.g., Sales or Supply Chain pages).
* **Embedded Self-Service:** Allowing power users to edit and save report variations without leaving the secure application environment.`,
  };
};

export default PowerBIManager;
